import React, {useState, useEffect, useCallback} from 'react';
import {
    Box,
    Typography,
    TextField,
    FormControl,
    FormLabel,
    RadioGroup,
    Radio,
    FormControlLabel,
    CircularProgress,
    makeStyles,
    FormHelperText,
} from '@material-ui/core';
import {Question} from '@agenium-ai/agenium-client-sdk/dist/models/question';
import MultipleChoiceSingleInput from '@/UI/EmcApplicationQuestions/inputs/MultipleChoiceSingleInput';
import MultipleChoiceMultipleInput from '@/UI/EmcApplicationQuestions/inputs/MultipleChoiceMultipleInput';
import {QuestionType} from '@agenium-ai/agenium-client-sdk/dist/enum/enum';
import {AgenuimContextType} from '@/Core/agenium/ageniumContext';
import {useBankVerification} from '@/Agent/Common/useBankVerification';
import {BankVerificationError} from '@/Agent/Common/BankVerificationError';
import {ParameterStore} from '@/Core/mobx/ParameterStore/ParameterStore';
import {ContactFormStore} from '@/Agent/ContactForm/ContactFormStore/ContactFormStore';
import {AccountType} from '@/Agent/types';
import {BankVerificationState} from '@/APIs/types';
import RootStore from '@/Core/mobx/RootStore';
import {Parameter} from '@/Core/mobx/ParameterStore/Parameter';
import {updateHubspotProsperDealBankingInfoProvidedUpfront} from '@/Agent/Quotes/deal';

interface PremiumSourceWithBankingProps {
    question: Question;
    ageniumContext: AgenuimContextType;
    parameterStore: ParameterStore;
    contactFormStore: ContactFormStore;
    classes: any;
}

const useStyles = makeStyles(() => ({
    bankingSection: {
        marginTop: '40px',
        paddingTop: '32px',
        borderTop: '1px solid #E0E0E0',
    },
    sectionTitle: {
        fontFamily: 'LoraRegular',
        fontWeight: 600,
        fontSize: '24px',
        lineHeight: '32px',
        color: '#456d69',
        marginBottom: '24px',
    },
    bankingField: {
        marginBottom: '24px',
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: '#174943',
            },
            '&:hover fieldset': {
                borderColor: '#174943',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#174943',
            },
        },
    },
    fieldLabel: {
        fontFamily: 'FredokaRegular',
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '24px',
        color: '#174943',
        marginBottom: '8px',
    },
    radioGroup: {
        flexDirection: 'row',
        gap: '24px',
        '& .MuiFormControlLabel-label': {
            fontFamily: 'FredokaRegular',
            fontSize: '16px',
            color: '#434343',
        },
    },
    otherField: {
        marginTop: '16px',
    },
}));

export const PremiumSourceWithBanking: React.FC<PremiumSourceWithBankingProps> = ({
    question,
    ageniumContext,
    parameterStore,
    contactFormStore,
    classes,
}) => {
    const localClasses = useStyles();
    const [routingNumber, setRoutingNumber] = useState(parameterStore.banking.routingNumber?.value || '');
    const [accountNumber, setAccountNumber] = useState(parameterStore.banking.accountNumber?.value || '');
    const [accountType, setAccountType] = useState<'checking' | 'savings'>(
        (parameterStore.banking.accountType?.value?.toLowerCase() as 'checking' | 'savings') || 'checking',
    );
    const [validationErrors, setValidationErrors] = useState<{
        banking?: string;
    }>({});
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

    // Bank verification hook
    const {
        verificationResult,
        overrideConfirmed,
        setOverrideConfirmed,
        isVerifying,
        verifyBankAccount,
        resetVerification,
        canProceed,
    } = useBankVerification();

    // Check if source is "Other"
    const isOtherSelected = ageniumContext.currentValue === 'Other';

    // This is a hack to retrigger validation for this one-off component
    useEffect(() => {
        if (ageniumContext.currentValue) {
            question.value = ageniumContext.currentValue;
            ageniumContext.setIsValidated(true);
        }
    }, []);

    // Reset verification when bank info changes
    useEffect(() => {
        resetVerification();
        setValidationErrors({});
        setHasAttemptedSubmit(false);
    }, [routingNumber, accountNumber, accountType]);

    // Validate banking fields
    const validateBankingFields = useCallback(() => {
        const errors: typeof validationErrors = {};

        // If one banking field is filled, both become required
        if ((routingNumber || accountNumber) && (!routingNumber || !accountNumber)) {
            errors.banking = 'Both routing number and account number are required';
            return errors;
        }

        // Validate routing number format (9 digits)
        if (routingNumber && !/^\d{9}$/.test(routingNumber)) {
            errors.banking = 'Routing number must be exactly 9 digits';
            return errors;
        }

        return errors;
    }, [routingNumber, accountNumber]);

    // Handle routing number change
    const handleRoutingNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 9);
        setRoutingNumber(value);
        parameterStore.banking.routingNumber.setValue(value);
    };

    // Handle account number change
    const handleAccountNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 17);
        setAccountNumber(value);
        parameterStore.banking.accountNumber.setValue(value);
    };

    // Handle account type change
    const handleAccountTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value as 'checking' | 'savings';
        setAccountType(value);
        parameterStore.banking.accountType.setValue(value.toUpperCase());
    };

    // Perform bank verification
    const performBankVerification = useCallback(async () => {
        console.log('performBankVerification called', {routingNumber, accountNumber});

        // Mark that we've attempted to submit
        setHasAttemptedSubmit(true);

        if (!routingNumber && !accountNumber) {
            console.log('No banking info provided, allowing to proceed');
            return {success: true}; // No banking info, allow to proceed
        }

        const errors = validateBankingFields();
        if (Object.keys(errors).length > 0) {
            console.log('Validation errors:', errors);
            setValidationErrors(errors);
            return {success: false, error: errors.banking || 'Please complete all required banking fields'};
        }

        console.log('Calling GIACT verification...');
        const accountTypeNumber = accountType === 'checking' ? 1 : 2;

        const result = await verifyBankAccount({
            routingNumber,
            accountNumber,
            accountType: accountTypeNumber,
            customerEmail: parameterStore.contact || contactFormStore.email || '',
            customerGuid: parameterStore.walkthroughData?.uuid || parameterStore.walkthroughData?.id,
            formId: parameterStore.formid,
        });

        console.log('GIACT verification result:', result);

        if (!result.success) {
            const errorMessage =
                'Bank verification failed. Please check your information or speak with the client to confirm.';
            setValidationErrors({
                banking: errorMessage,
            });

            // Check if we can proceed with override
            if ((result as any).result?.state === BankVerificationState.PREVENT_NEXT_WITH_OVERRIDE) {
                // If override is confirmed, allow to proceed
                if (overrideConfirmed) {
                    console.log('Proceeding with override');
                    return {success: true};
                }
                return {success: false, error: errorMessage};
            }

            return {success: false, error: errorMessage};
        } else {
            console.log('GIACT verification passed');
            // Save verification status
            if (!(parameterStore.approval as any).bankingGiactPassPreApp) {
                (parameterStore.approval as any).bankingGiactPassPreApp = new Parameter('bankingGiactPassPreApp');
            }
            (parameterStore.approval as any).bankingGiactPassPreApp.value = true;

            if (!(parameterStore.approval as any).bankingCollectedPreApp) {
                (parameterStore.approval as any).bankingCollectedPreApp = new Parameter('bankingCollectedPreApp');
            }
            (parameterStore.approval as any).bankingCollectedPreApp.value = true;

            // Track that banking info was provided upfront
            if (!(parameterStore.approval as any).bankingProvidedUpfront) {
                (parameterStore.approval as any).bankingProvidedUpfront = new Parameter('bankingProvidedUpfront');
            }
            (parameterStore.approval as any).bankingProvidedUpfront.value = true;

            // Update Hubspot deal in background (don't block the flow)
            updateHubspotProsperDealBankingInfoProvidedUpfront('Yes')
                .then(() => {
                    console.log('Updated Hubspot deal: banking info provided upfront = Yes');
                })
                .catch((error) => {
                    console.error('Failed to update Hubspot deal with banking info:', error);
                    // Continue anyway - error doesn't affect the flow
                });

            return {success: true};
        }
    }, [
        routingNumber,
        accountNumber,
        accountType,
        validateBankingFields,
        verifyBankAccount,
        parameterStore,
        contactFormStore,
        overrideConfirmed,
    ]);

    // Store verification function in agenium context for Next button
    useEffect(() => {
        console.log('Attaching bankVerificationFunction to context');
        (ageniumContext as any).bankVerificationFunction = performBankVerification;
        (ageniumContext as any).canProceedWithBanking = () => {
            // If no banking info, can proceed
            if (!routingNumber && !accountNumber) {
                return true;
            }
            // Check if validation would pass
            const errors = validateBankingFields();
            if (Object.keys(errors).length > 0) {
                return false;
            }
            // Check verification status
            if (verificationResult) {
                return canProceed();
            }
            // No verification yet, allow to proceed (will verify on Next)
            return true;
        };

        return () => {
            console.log('Cleaning up bankVerificationFunction');
            delete (ageniumContext as any).bankVerificationFunction;
            delete (ageniumContext as any).canProceedWithBanking;
        };
    }, [
        performBankVerification,
        validateBankingFields,
        verificationResult,
        canProceed,
        routingNumber,
        accountNumber,
        ageniumContext,
    ]);

    // Handle Other source description
    useEffect(() => {
        // Look for the OtherPremiumSource question if "Other" is selected
        if (isOtherSelected) {
            // Store the other source value in context for the next question
            (ageniumContext as any).otherPremiumSourcePending = true;
        }
    }, [isOtherSelected, ageniumContext]);

    return (
        <>
            {/* Premium Source Question - render based on question type */}
            {question.questionType === QuestionType.MultipleChoiceMultipleAnswer ? (
                <MultipleChoiceMultipleInput
                    value={ageniumContext.currentValue as string}
                    onChange={ageniumContext.setCurrentValue}
                    question={question}
                />
            ) : (
                <MultipleChoiceSingleInput
                    value={ageniumContext.currentValue}
                    onChange={(value) => ageniumContext.setCurrentValue(value)}
                    question={question}
                    isInternalQuestion={false}
                />
            )}
            {/* Banking Information Section */}
            <Box className={localClasses.bankingSection}>
                <Typography className={localClasses.sectionTitle}>Verify U.S. Bank Info (Optional)</Typography>

                <Box>
                    <Box className={localClasses.bankingField}>
                        <Typography className={localClasses.fieldLabel}>Bank Routing Number</Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="9-digit routing number"
                            value={routingNumber}
                            onChange={handleRoutingNumberChange}
                            error={hasAttemptedSubmit && !!validationErrors.banking && !!routingNumber}
                            helperText={
                                hasAttemptedSubmit && routingNumber && routingNumber.length !== 9
                                    ? '9 digits required'
                                    : ''
                            }
                        />
                    </Box>

                    <Box className={localClasses.bankingField}>
                        <Typography className={localClasses.fieldLabel}>Bank Account Number</Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Account number"
                            value={accountNumber}
                            onChange={handleAccountNumberChange}
                            error={hasAttemptedSubmit && !!validationErrors.banking && !!accountNumber}
                        />
                    </Box>

                    <Box className={localClasses.bankingField}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend" className={localClasses.fieldLabel}>
                                Account Type
                            </FormLabel>
                            <RadioGroup
                                value={accountType}
                                onChange={handleAccountTypeChange}
                                className={localClasses.radioGroup}
                            >
                                <FormControlLabel
                                    value="checking"
                                    control={<Radio color="primary" />}
                                    label="Checking"
                                />
                                <FormControlLabel value="savings" control={<Radio color="primary" />} label="Savings" />
                            </RadioGroup>
                        </FormControl>
                    </Box>

                    {hasAttemptedSubmit && validationErrors.banking && (
                        <FormHelperText error style={{marginBottom: '16px', fontSize: '14px'}}>
                            {validationErrors.banking}
                        </FormHelperText>
                    )}

                    {verificationResult && (
                        <Box style={{marginBottom: '16px'}}>
                            <BankVerificationError
                                verificationResult={verificationResult}
                                overrideConfirmed={overrideConfirmed}
                                onOverrideChange={setOverrideConfirmed}
                            />
                        </Box>
                    )}

                    {isVerifying && (
                        <Box display="flex" alignItems="center" style={{gap: '8px', marginBottom: '16px'}}>
                            <CircularProgress size={20} />
                            <Typography variant="body2">Verifying bank account...</Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </>
    );
};
